/**
 * Vista pura del resumen financiero 4I (sin React).
 * Fuente: LoanFinancialSnapshot — no recalcula totales.
 */
import type { LoanFinancialSnapshotV1 } from './loan-financial.types';
import { formatBusinessDate, formatMoneyAr } from './loan-financial.format';

export type SummaryCard = {
  id: 'originalPrincipal' | 'pendingPrincipal' | 'overdue' | 'contractualTotal';
  label: string;
  value: string;
  helper: string;
  emphasize?: boolean;
  tooltip?: string;
};

export type BreakdownRow = { label: string; value: string; tooltip?: string };

export function buildFinancialSummaryCards(snap: LoanFinancialSnapshotV1): SummaryCard[] {
  const asOf = formatBusinessDate(snap.asOf);
  const overdueTotal = Number(snap.overdue?.total ?? 0);
  return [
    {
      id: 'originalPrincipal',
      label: 'Capital original',
      value: formatMoneyAr(snap.originalPrincipal),
      helper: 'Monto originalmente otorgado',
    },
    {
      id: 'pendingPrincipal',
      label: 'Capital pendiente',
      value: formatMoneyAr(snap.contractual.principal),
      helper: 'Capital que todavía no fue cancelado',
      emphasize: true,
    },
    {
      id: 'overdue',
      label: 'Deuda vencida',
      value: formatMoneyAr(overdueTotal),
      helper: `Cuotas vencidas pendientes al ${asOf}`,
      emphasize: overdueTotal > 0,
    },
    {
      id: 'contractualTotal',
      label: 'Total contractual pendiente',
      value: formatMoneyAr(snap.contractual.total),
      helper: 'Capital + intereses + cargos + punitorios pendientes del plan',
      tooltip:
        'Este importe incluye conceptos de cuotas futuras y no representa necesariamente el saldo contable ni el monto de cancelación anticipada.',
    },
  ];
}

export function buildContractualBreakdownRows(snap: LoanFinancialSnapshotV1): BreakdownRow[] {
  return [
    {
      label: 'Capital',
      value: formatMoneyAr(snap.contractual.principal),
    },
    {
      label: 'Intereses',
      value: formatMoneyAr(snap.contractual.interest),
      tooltip: 'Intereses previstos en las cuotas pendientes del plan.',
    },
    {
      label: 'Cargos',
      value: formatMoneyAr(snap.contractual.fees),
      tooltip:
        'Puede incluir administración, seguro, impuestos, terceros u otros según el producto.',
    },
    {
      label: 'Punitorios',
      value: formatMoneyAr(snap.contractual.penalties),
      tooltip: 'Importes generados por mora según la política vigente del préstamo.',
    },
    {
      label: 'Total',
      value: formatMoneyAr(snap.contractual.total),
    },
  ];
}

export function buildAccountingSummaryRows(snap: LoanFinancialSnapshotV1): BreakdownRow[] {
  return [
    {
      label: 'Capital por cobrar',
      value: formatMoneyAr(snap.accounting.principalReceivable),
    },
    {
      label: 'Intereses devengados por cobrar',
      value: formatMoneyAr(snap.accounting.interestReceivable),
      tooltip: 'Intereses ya reconocidos contablemente que todavía no fueron cobrados.',
    },
    {
      label: 'Otros créditos',
      value: formatMoneyAr(snap.accounting.otherReceivables),
    },
    {
      label: 'Saldo contable',
      value: formatMoneyAr(snap.accounting.totalReceivable),
    },
  ];
}

export function buildCollectedRows(snap: LoanFinancialSnapshotV1): BreakdownRow[] {
  return [
    { label: 'Capital', value: formatMoneyAr(snap.collected.principal) },
    { label: 'Intereses', value: formatMoneyAr(snap.collected.interest) },
    { label: 'Cargos', value: formatMoneyAr(snap.collected.fees) },
    { label: 'Punitorios', value: formatMoneyAr(snap.collected.penalties) },
    {
      label: 'Crédito no aplicado',
      value: formatMoneyAr(snap.collected.unapplied),
      tooltip:
        'Importe no imputado a cuotas; queda como crédito del cliente según política.',
    },
    { label: 'Total recibido', value: formatMoneyAr(snap.collected.total) },
  ];
}

/** Fixture del caso original 4I (sin pagos, ON_COLLECTION). */
export const ORIGINAL_CASE_SNAPSHOT: LoanFinancialSnapshotV1 = {
  schemaVersion: 1,
  loanId: 'fixture-original',
  asOf: '2026-09-29',
  currency: 'ARS',
  timezone: 'America/Argentina/Buenos_Aires',
  originalPrincipal: 1_500_000,
  contractual: {
    principal: 1_500_000,
    interest: 2_523_600,
    fees: 484_200,
    penalties: 0,
    total: 4_507_800,
  },
  overdue: {
    principal: 0,
    interest: 0,
    fees: 0,
    penalties: 0,
    total: 0,
    installmentCount: 0,
    pastDueInstallmentCount: 0,
    dueTodayInstallmentCount: 0,
  },
  accounting: {
    principalReceivable: 1_500_000,
    interestReceivable: 0,
    feeReceivable: 0,
    feeReceivableStatus: 'NOT_APPLICABLE_CURRENT_POLICY',
    otherReceivables: 0,
    otherReceivablesDetail: { payrollReceivable: 0 },
    totalReceivable: 1_500_000,
  },
  collected: {
    principal: 0,
    interest: 0,
    fees: 0,
    penalties: 0,
    unapplied: 0,
    total: 0,
  },
  payoff: { available: true, policy: 'ACCRUED_TO_DATE' },
  reconciliation: { status: 'OK', findingsCount: 0, findings: [] },
  interestRecognitionMethod: 'ON_COLLECTION',
};

/** Assertions del caso visual obligatorio (sin DOM). */
export function assertOriginalCaseDisplay(snap: LoanFinancialSnapshotV1 = ORIGINAL_CASE_SNAPSHOT): string[] {
  const cards = buildFinancialSummaryCards(snap);
  const accounting = buildAccountingSummaryRows(snap);
  const errors: string[] = [];

  const byId = Object.fromEntries(cards.map((c) => [c.id, c]));
  if (byId.originalPrincipal?.value !== formatMoneyAr(1_500_000)) {
    errors.push('Capital original incorrecto');
  }
  if (byId.pendingPrincipal?.value !== formatMoneyAr(1_500_000)) {
    errors.push('Capital pendiente incorrecto');
  }
  if (byId.overdue?.value !== formatMoneyAr(0)) {
    errors.push('Deuda vencida incorrecta');
  }
  if (byId.contractualTotal?.value !== formatMoneyAr(4_507_800)) {
    errors.push('Total contractual incorrecto');
  }
  const saldo = accounting.find((r) => r.label === 'Saldo contable');
  if (saldo?.value !== formatMoneyAr(1_500_000)) {
    errors.push('Saldo contable incorrecto');
  }
  // Guard: no confundir contractual con "a cancelar hoy"
  if (byId.contractualTotal?.label.toLowerCase().includes('cancelar hoy')) {
    errors.push('Label contractual ambiguo (cancelar hoy)');
  }
  return errors;
}
